import type { ChangeEvent } from 'react';
import { Search } from 'lucide-react';
import { cn } from '~/utils';

export type ScopeOption<TScope extends string> = {
  value: TScope;
  label: string;
  testId: string;
};

type Props<TScope extends string> = {
  searchTerm: string;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  searchPlaceholder: string;
  searchTestId: string;
  scope?: {
    value: TScope;
    onChange: (scope: TScope) => void;
    options: ScopeOption<TScope>[];
  };
};

export function ListToolbar<TScope extends string>({
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  searchTestId,
  scope,
}: Props<TScope>) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
      <div className="relative flex-1">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-neut-400"
        />
        <input
          type="search"
          data-testid={searchTestId}
          value={searchTerm}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          autoComplete="off"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-neut-300 text-sm text-neut-700 focus:outline-none focus:ring-2 focus:ring-prim"
        />
      </div>
      {scope && (
        <div className="flex items-center gap-2">
          {scope.options.map((option) => {
            const isActive = scope.value === option.value;
            return (
              <button
                key={option.value}
                type="button"
                data-testid={option.testId}
                aria-pressed={isActive}
                onClick={() => scope.onChange(option.value)}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-prim text-white'
                    : 'border border-neut-300 text-neut-700 hover:bg-neut-50'
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
